import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, UserPlus, Wallet, Trophy, Banknote } from "lucide-react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { WHATSAPP_LINK, getInitialLang, saveLang } from "../i18n";

const copy = {
  en: {
    kicker: "How To Play",
    title: "START WINNING IN 4 STEPS",
    sub: "From sign-up to withdrawal — the whole journey takes minutes, not days.",
    steps: [
      { icon: UserPlus, t: "Register on WhatsApp", d: "Tap any Join button — our team creates your Cric1x ID instantly, free of cost." },
      { icon: Wallet, t: "Deposit via UPI", d: "Add funds in seconds with UPI, Paytm, PhonePe, GPay or bank transfer. 7% bonus on your first deposit." },
      { icon: Trophy, t: "Play & Predict", d: "Bet on live cricket matches with the sharpest odds. Every deposit earns 5% extra." },
      { icon: Banknote, t: "Withdraw Instantly", d: "Cash out winnings in minutes, 24x7. Plus 10% weekly loss cashback keeps you covered." },
    ],
    faq_title: "Quick Questions",
    faqs: [
      { q: "Is registration free?", a: "Yes, 100% free. Your ID is created on WhatsApp within minutes." },
      { q: "What is the minimum deposit?", a: "Message us on WhatsApp — our team will share current minimums and active bonus offers." },
      { q: "How fast are withdrawals?", a: "Most withdrawals are processed within minutes, any time of day." },
      { q: "How do I get the 10% cashback?", a: "It's automatic — up to 10% of your weekly losses is credited back every Monday." },
    ],
    cta: "Start Now on WhatsApp",
  },
  hi: {
    kicker: "कैसे खेलें",
    title: "4 स्टेप में जीतना शुरू करें",
    sub: "साइन-अप से विदड्रॉल तक — पूरा सफर मिनटों में, दिनों में नहीं।",
    steps: [
      { icon: UserPlus, t: "WhatsApp पर रजिस्टर करें", d: "कोई भी Join बटन दबाएं — हमारी टीम तुरंत आपकी Cric1x ID फ्री में बना देगी।" },
      { icon: Wallet, t: "UPI से डिपॉज़िट करें", d: "UPI, Paytm, PhonePe, GPay या बैंक ट्रांसफर से सेकंडों में पैसे डालें। पहली डिपॉज़िट पर 7% बोनस।" },
      { icon: Trophy, t: "खेलें और जीतें", d: "लाइव क्रिकेट मैचों पर बेस्ट ऑड्स के साथ खेलें। हर डिपॉज़िट पर 5% एक्स्ट्रा।" },
      { icon: Banknote, t: "तुरंत विदड्रॉ करें", d: "जीत मिनटों में निकालें, 24x7। साथ में 10% वीकली लॉस कैशबैक।" },
    ],
    faq_title: "जल्दी जवाब",
    faqs: [
      { q: "क्या रजिस्ट्रेशन फ्री है?", a: "हाँ, 100% फ्री। आपकी ID WhatsApp पर मिनटों में बन जाती है।" },
      { q: "मिनिमम डिपॉज़िट कितना है?", a: "WhatsApp पर मैसेज करें — टीम आपको मौजूदा मिनिमम और एक्टिव बोनस बताएगी।" },
      { q: "विदड्रॉल कितना तेज़ है?", a: "ज़्यादातर विदड्रॉल मिनटों में प्रोसेस होते हैं, दिन-रात कभी भी।" },
      { q: "10% कैशबैक कैसे मिलेगा?", a: "ऑटोमैटिक — हर सोमवार आपके वीकली लॉस का 10% तक वापस क्रेडिट होता है।" },
    ],
    cta: "WhatsApp पर शुरू करें",
  },
};

export default function HowToPlay() {
  const [lang, setLangState] = useState(getInitialLang);
  const setLang = (l) => {
    saveLang(l);
    setLangState(l);
  };
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-obsidian font-body text-white">
      <div className="noise-overlay" />
      <SiteHeader lang={lang} setLang={setLang} />

      <main className="mx-auto max-w-7xl px-5 pb-24 pt-32" data-testid="how-to-play-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">{t.kicker}</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            {t.title}
          </h1>
          <p className="mt-5 max-w-xl text-white/60">{t.sub}</p>
        </motion.div>

        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {t.steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
              className="bg-surface p-8"
              data-testid={`step-card-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <s.icon className="text-gold" size={28} />
                <span className="font-display text-5xl font-bold text-white/10">0{i + 1}</span>
              </div>
              <p className="mt-6 font-display text-xl font-semibold uppercase">{s.t}</p>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-3">
          {["UPI", "Paytm", "PhonePe", "GPay", "Net Banking", "Bank Transfer"].map((p) => (
            <span key={p} className="border border-white/10 bg-surface px-5 py-3 font-display text-sm font-medium uppercase tracking-wide text-white/70">
              {p}
            </span>
          ))}
        </div>

        <div className="mt-20">
          <p className="font-mono text-[12px] uppercase tracking-[0.35em] text-gold">{t.faq_title}</p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {t.faqs.map((f, i) => (
              <div key={i} className="border border-white/10 bg-surface p-7" data-testid={`faq-${i + 1}`}>
                <p className="font-display text-lg font-semibold">{f.q}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noreferrer"
          className="mt-14 inline-flex items-center gap-2 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[0.97] gold-glow"
          data-testid="howto-whatsapp-cta"
        >
          <MessageCircle size={18} /> {t.cta}
        </a>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}
